import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, MapPin, QrCode, ShieldCheck, Sparkles, Users } from 'lucide-react';
import { api } from '@/services/api';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Toggle } from '@/components/ui/toggle';
import { useToast } from '@/components/ui/toast';

const inviteTypes = [
  ['club_member', 'Club Member', 'Permanent club membership'],
  ['convoy_rider', 'Convoy Rider', 'Join one convoy session'],
  ['guest_rider', 'Guest Rider', 'Temporary ride access'],
  ['club_collaboration', 'Club Collaboration', 'Invite another club'],
  ['staff', 'Staff', 'Moderator or ride support'],
];

export function CreateRidePassPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState({
    inviteType: 'club_member', title: '', groupId: '', tripId: '', role: 'member',
    approvalRequired: true, expiresInHours: 24, usageLimit: 20, allowedDomain: '',
    meetingLabel: '', longitude: '', latitude: '', checkInRadiusMeters: 300,
  });
  const groups = useQuery({ queryKey: ['invite-groups'], queryFn: () => api('/api/v1/admin/groups').then((result) => result.data || []) });
  const trips = useQuery({ queryKey: ['invite-trips'], queryFn: () => api('/api/v1/admin/trips').then((result) => result.data || []) });
  const requiresTrip = ['convoy_rider', 'guest_rider', 'club_collaboration'].includes(form.inviteType);
  const requiresGroup = ['club_member', 'staff', 'club_collaboration'].includes(form.inviteType);
  const valid = form.title.trim().length >= 2 && (!requiresGroup || form.groupId) && (!requiresTrip || form.tripId);
  const selectedType = useMemo(() => inviteTypes.find(([key]) => key === form.inviteType), [form.inviteType]);

  const create = useMutation({
    mutationFn: async () => {
      const longitude = Number(form.longitude);
      const latitude = Number(form.latitude);
      const hasMeetingPoint = Number.isFinite(longitude) && Number.isFinite(latitude) && form.longitude !== '' && form.latitude !== '';
      return api('/api/v1/admin/invite-hub/passes', {
        method: 'POST',
        body: JSON.stringify({
          inviteType: form.inviteType,
          title: form.title.trim(),
          groupId: form.groupId || undefined,
          tripId: form.tripId || undefined,
          role: form.role,
          approvalRequired: form.approvalRequired,
          expiresInHours: Number(form.expiresInHours),
          usageLimit: Number(form.usageLimit),
          allowedDomain: form.allowedDomain.trim(),
          checkInRadiusMeters: Number(form.checkInRadiusMeters),
          meetingPoint: hasMeetingPoint ? { type: 'Point', coordinates: [longitude, latitude], label: form.meetingLabel.trim() || undefined } : undefined,
          sourceLabel: 'dashboard',
        }),
      }).then((result) => result.data);
    },
    onSuccess: (pass) => {
      toast.show('Ride Pass created', 'success');
      navigate(`/invite-hub/${pass.id}`);
    },
    onError: (cause) => toast.show(cause.message, 'error', 'Unable to create Ride Pass'),
  });

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  return <div className="workspace-page">
    <PageHeader
      eyebrow="Invite Hub"
      title="Create Ride Pass"
      description="Set who can join, when the pass expires, and whether approval is required."
      actions={<Button variant="secondary" onClick={() => navigate('/invite-hub')}><ArrowLeft size={14}/>Back</Button>}
    />
    <div className="create-pass-grid workspace-fill scrollbar overflow-y-auto">
      <section className="create-pass-panel">
        <h2>Pass type</h2>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {inviteTypes.map(([key, label, description]) => <button type="button" key={key} onClick={() => update('inviteType', key)} className={`pass-type-option ${form.inviteType === key ? 'pass-type-option--active' : ''}`}>
            <span><strong>{label}</strong><small>{description}</small></span>{form.inviteType === key && <Check size={15}/>} 
          </button>)}
        </div>
      </section>

      <section className="create-pass-panel space-y-4">
        <div><label>Pass title</label><Input value={form.title} onChange={(event) => update('title', event.target.value)} placeholder="Bandung Night Ride"/></div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><label>Club</label><Select value={form.groupId} onChange={(event) => update('groupId', event.target.value)}><option value="">{requiresGroup ? 'Select club' : 'Optional club'}</option>{(groups.data || []).map((group) => <option key={group._id || group.id} value={group._id || group.id}>{group.name}</option>)}</Select></div>
          <div><label>Convoy</label><Select value={form.tripId} onChange={(event) => update('tripId', event.target.value)}><option value="">{requiresTrip ? 'Select convoy' : 'Optional convoy'}</option>{(trips.data || []).map((trip) => <option key={trip._id || trip.id} value={trip._id || trip.id}>{trip.name}</option>)}</Select></div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div><label>Role</label><Select value={form.role} onChange={(event) => update('role', event.target.value)}><option value="member">Rider</option><option value="sweeper">Sweeper</option><option value="sub_convoy_leader">Sub-Convoy Leader</option><option value="medic">Medic</option><option value="mechanic">Mechanic</option><option value="marshal">Marshal</option><option value="moderator">Club Moderator</option></Select></div>
          <div><label>Expires</label><Select value={form.expiresInHours} onChange={(event) => update('expiresInHours', event.target.value)}><option value="6">6 hours</option><option value="12">12 hours</option><option value="24">24 hours</option><option value="72">3 days</option><option value="168">7 days</option></Select></div>
          <div><label>Usage limit</label><Input type="number" min="1" max="5000" value={form.usageLimit} onChange={(event) => update('usageLimit', event.target.value)}/></div>
        </div>
        <div><label>Allowed Google domain</label><Input value={form.allowedDomain} onChange={(event) => update('allowedDomain', event.target.value)} placeholder="Optional, example.com"/></div>
        <div className="create-pass-toggle"><span><ShieldCheck size={16}/><span><strong>Owner approval</strong><small>Review riders before access is granted.</small></span></span><Toggle checked={form.approvalRequired} onChange={(value) => update('approvalRequired', value)}/></div>
      </section>

      <section className="create-pass-panel space-y-4">
        <div className="flex items-center gap-2"><MapPin size={16} className="text-cyan-300"/><h2>Meeting point and check-in</h2></div>
        <div><label>Location name</label><Input value={form.meetingLabel} onChange={(event) => update('meetingLabel', event.target.value)} placeholder="Rest Area KM 19"/></div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div><label>Longitude</label><Input inputMode="decimal" value={form.longitude} onChange={(event) => update('longitude', event.target.value)} placeholder="106.816"/></div>
          <div><label>Latitude</label><Input inputMode="decimal" value={form.latitude} onChange={(event) => update('latitude', event.target.value)} placeholder="-6.200"/></div>
          <div><label>Check-in radius</label><Input type="number" min="25" max="5000" value={form.checkInRadiusMeters} onChange={(event) => update('checkInRadiusMeters', event.target.value)}/></div>
        </div>
      </section>

      <aside className="create-pass-summary">
        <div className="create-pass-summary__glyph"><QrCode size={34}/><span/></div>
        <p className="eyebrow">Ride Pass preview</p>
        <h2>{form.title || 'Untitled Ride Pass'}</h2>
        <p>{selectedType?.[1]}</p>
        <div className="mt-6 space-y-3 text-xs">
          <div><Users size={14}/><span>Up to {form.usageLimit || 0} riders</span></div>
          <div><ShieldCheck size={14}/><span>{form.approvalRequired ? 'Approval required' : 'Instant access'}</span></div>
          <div><MapPin size={14}/><span>{form.meetingLabel || 'Meeting point optional'}</span></div>
        </div>
        <Button className="mt-8 w-full" disabled={!valid || create.isPending} onClick={() => create.mutate()}><Sparkles size={15}/>{create.isPending ? 'Creating...' : 'Generate Ride Pass'}</Button>
      </aside>
    </div>
  </div>;
}
